import { Suspense, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import * as Popover from '@givebutter/primitives/popover';
import { SearchHit } from 'api/search';
import { CampaignResults } from 'components/tasks/form/links/CampaignResults';
import { CompanyResults } from 'components/tasks/form/links/CompanyResults';
import { ContactResults } from 'components/tasks/form/links/ContactResults';
import { EmptyResults } from 'components/tasks/form/links/EmptyResults';
import { TicketResults } from 'components/tasks/form/links/TicketResults';
import { TransactionResults } from 'components/tasks/form/links/TransactionResults';
import { removeTrigger } from 'components/tasks/utils/editor-search-helpers';
import { generateTaggedResourceHtml } from 'components/tasks/utils/generateTaggedResourceHtml';
import { useLinkSearchStore } from 'components/tasks/utils/useLinkSearchStore';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { Editor } from 'tinymce';
import { Campaign, Contact, Ticket, Transaction } from 'types/domain';

interface LinkPickerProps extends Popover.PopoverProps {
    editorInstance: Editor | null;
    onSelectItem: () => void;
}

const LinkPicker = ({ editorInstance, onSelectItem, children, ...rest }: LinkPickerProps) => {
    const { search_api_key } = useCurrentAccount();
    const { clear, listRef } = useLinkSearchStore(({ query, clear, listRef }) => ({
        query,
        clear,
        listRef,
    }));

    const handleSelection = useCallback(
        (content: string) => {
            if (!editorInstance) return;
            const bookmark = editorInstance.selection.getBookmark();
            editorInstance.focus();
            removeTrigger(editorInstance.selection, bookmark);
            editorInstance?.execCommand('mceInsertContent', false, content);
            clear();
            onSelectItem();
        },
        [editorInstance, onSelectItem, clear],
    );

    return (
        <Popover.Root {...rest} modal={false}>
            <Popover.Anchor asChild>{children}</Popover.Anchor>
            <Popover.Portal>
                <Popover.Content
                    className="w-96 overflow-auto p-2"
                    align="start"
                    side="bottom"
                    onOpenAutoFocus={e => e.preventDefault()}
                >
                    <Suspense fallback={null}>
                        <label htmlFor="task_link_search_results" className={'sr-only'}>
                            Search results
                        </label>
                        <div
                            id="task_link_search_results"
                            role="listbox"
                            aria-label="Search results"
                            className="flex flex-col gap-1 px-2 pb-2"
                            ref={listRef}
                        >
                            <ErrorBoundary
                                resetKeys={[search_api_key]}
                                fallback={
                                    <AlertNotification.Root type={'error'} className={'my-2'}>
                                        We're having trouble making your request. Please try again
                                        in a few minutes.
                                    </AlertNotification.Root>
                                }
                            >
                                <ContactResults
                                    onSelect={(hit: SearchHit<Contact>) => {
                                        handleSelection(
                                            generateTaggedResourceHtml({
                                                type: 'contact',
                                                resource: hit.document,
                                            }),
                                        );
                                    }}
                                />
                                <CompanyResults
                                    onSelect={(hit: SearchHit<Contact>) => {
                                        handleSelection(
                                            generateTaggedResourceHtml({
                                                type: 'contact',
                                                resource: hit.document,
                                            }),
                                        );
                                    }}
                                />
                                <TransactionResults
                                    onSelect={(
                                        hit: SearchHit<
                                            Omit<Transaction, 'line_items' | 'transactions'>
                                        >,
                                    ) => {
                                        handleSelection(
                                            generateTaggedResourceHtml({
                                                type: 'transaction',
                                                resource: hit.document,
                                            }),
                                        );
                                    }}
                                />
                                <CampaignResults
                                    onSelect={(hit: SearchHit<Campaign>) => {
                                        handleSelection(
                                            generateTaggedResourceHtml({
                                                type: 'campaign',
                                                resource: hit.document,
                                            }),
                                        );
                                    }}
                                />
                                <TicketResults
                                    onSelect={(hit: SearchHit<Ticket>) => {
                                        handleSelection(
                                            generateTaggedResourceHtml({
                                                type: 'ticket',
                                                resource: hit.document,
                                            }),
                                        );
                                    }}
                                />
                            </ErrorBoundary>
                        </div>
                        <ErrorBoundary fallback={null} resetKeys={[search_api_key]}>
                            <EmptyResults />
                        </ErrorBoundary>
                    </Suspense>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export { LinkPicker };
